/* import __COLOCATED_TEMPLATE__ from './seller-posting-hybrid-select.hbs'; */
import clsx from 'clsx';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object';

const CLASS_NAME = 'seller-posting-hybrid-select';
const EMPTY_OPTIONS = [];

const getNextOption = (currentModel, options, increment) => {
  const currentSelectedIndex = options.findIndex(({ model }) => model === currentModel);
  let selectedIndex = currentSelectedIndex + increment;

  if (selectedIndex >= options.length) {
    selectedIndex = 0;
  } else if (selectedIndex < 0) {
    selectedIndex = options.length - 1;
  }

  return options[selectedIndex].model;
};

/**
 * Dropdown field with support for free text input when custom option selected
 */
class SellerPostingHybridSelectComponent extends Component {
  @tracked hasFocus = false;
  @tracked isDirty = false;
  @tracked selectedModel = this.args.selectedModel ?? -1;
  @tracked useCustomInput = Boolean(this.selectedOption?.custom);

  /**
   * @type {HTMLInputElement|null}
   */
  element = null;

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get options() {
    return this.args.options ?? EMPTY_OPTIONS;
  }

  get showChevron() {
    return !this.useCustomInput;
  }

  get showClear() {
    return this.useCustomInput;
  }

  get selectedIndex() {
    const { options, selectedModel } = this;

    return options.findIndex(({ model }) => model === selectedModel);
  }

  get selectedOption() {
    const { options, selectedIndex } = this;

    return options[selectedIndex];
  }

  get showOptions() {
    if (!this.hasFocus) return false;
    if (!this.isDirty) return false;
    if (this.useCustomInput) return false;

    const hasOptions = this.options.length > 0;
    const willShowEmptyIndicator = Boolean(this.args.value) && !this.args.isLoadingOptions;

    return hasOptions || willShowEmptyIndicator;
  }

  get readonly() {
    return Boolean(this.args.readonly || !this.useCustomInput);
  }

  get value() {
    return this.useCustomInput
      ? this.args.value
      : (this.selectedOption?.key ?? '');
  }

  /**
   *
   */
  @action
  handleChevronClick() {
    this.element.focus();
  }

  /**
   *
   */
  @action
  handleClearClick() {
    this.isDirty = false;
    this.selectedModel = -1;
    this.useCustomInput = false;

    this.args.onchange?.(null);
  }

  /**
   *
   */
  @action
  handleBlur() {
    this.hasFocus = false;
    this.isDirty = false;
    this.args.onblur?.();
  }

  /**
   *
   */
  @action
  handleClick() {
    this.isDirty = true;
  }

  /**
   *
   */
  @action
  handleFocus() {
    this.hasFocus = true;
    this.isDirty = true;
    this.args.onfocus?.();
  }

  /**
   * @param {string} value
   */
  @action
  handleInput(value) {
    this.isDirty = true;
    this.args.oninput?.(value);
  }

  /**
   * @param {KeyboardEvent} e
   */
  @action
  handleKeyDown(e) {
    this.args.onkeydown?.(e);

    if (e.defaultPrevented) {
      return;
    }

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        this.selectFromOptionsKeyboard(-1);
        break;

      case 'ArrowDown':
        e.preventDefault();
        this.selectFromOptionsKeyboard(+1);
        break;

      case 'Enter':
        const { selectedOption } = this;

        // done typing custom option
        const customInputInteract = Boolean(this.hasFocus && selectedOption?.custom && this.useCustomInput);

        if (customInputInteract) {
          this.element.blur();
        } else if (selectedOption) {
          e.preventDefault();

          // arrow keys making selection
          this.isDirty = false;

          if (selectedOption.custom) {
            this.useCustomInput = true;
            this.element.focus();
          } else {
            this.useCustomInput = false;
          }

          this.args.onchange?.(selectedOption);
        }

        break;

      case 'Escape':
        e.preventDefault();
        this.isDirty = false;
        break;
    }
  }

  /**
   * @param {Object} option
   */
  @action
  handleOptionClick(option) {
    this.selectedModel = option.model;

    this.isDirty = false;

    if (option.custom) {
      this.useCustomInput = true;
      this.element.focus();
    } else {
      this.useCustomInput = false;
    }

    this.args.onchange?.(option);
  }

  /**
   * @param {HTMLInputElement} element
   */
  @action
  refCallback(element) {
    this.element = element;
    this.args.ref?.(element);
  }

  /**
   * @param {number} increment
   * @private
   */
  selectFromOptionsKeyboard(increment) {
    this.isDirty = true;

    if (this.showOptions) {
      const { options, selectedModel } = this;

      if (options.length > 0) {
        this.selectedModel = getNextOption(selectedModel, options, increment);
      }
    }
  }
}

export default SellerPostingHybridSelectComponent;
